import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FormSummaryItem, Skeleton } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

interface IInitiatedByLineProps {
  /** The run's start date, already formatted. */
  date: string;
  /** The run's start time, already formatted. */
  time: string;
  /** The importer's `created_by`. Absent when no initiator was recorded. */
  userId?: string;
}

/**
 * The "Initiated by" and "Initiated on" items.
 *
 * Resolves `userId` through the `Users` collection, which caches by id, so a page of imports
 * started by the same person fetches them once. With no user id, or a user who can't be read
 * (removed from the account, or Feathr staff outside it), only "Initiated on" shows.
 */
function InitiatedByLine({ date, time, userId }: Readonly<IInitiatedByLineProps>): JSX.Element {
  const { t } = useTranslation();
  const { Users } = useStore();

  const user = userId ? Users.get(userId) : undefined;

  // `User.name` prefers first + last over the stale raw `name`, but falls back to "Unnamed User";
  // like UserSelect, use the email instead when the user has no name at all.
  const hasName = !!(user?.firstName || user?.lastName || user?.get('name', '').trim());
  const readableUser = user && !user.isErrored ? user : undefined;
  const name = hasName ? readableUser?.name : readableUser?.get('email', '').trim();
  const initiator: ReactNode = user?.isPending ? <Skeleton /> : name;

  // Values interpolate unescaped (`{{- }}`): React escapes the text, and i18next's own escaping
  // would print a date's slashes as HTML entities.

  return (
    <>
      {initiator ? <FormSummaryItem label={t('Initiated by')} value={initiator} /> : null}
      <FormSummaryItem
        label={t('Initiated on')}
        value={t('{{- date}} at {{- time}}', { date, time })}
      />
    </>
  );
}

export default observer(InitiatedByLine);
